import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { ResponseLoginWeb } from '../types/auth.types';
import { AuthService } from '../services/auth.service';

interface AuthContextType {
  user: ResponseLoginWeb | null;
  login: (userData: ResponseLoginWeb) => void;
  logout: () => void;
  isAuthenticated: boolean;
  checkAuthStatus: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ResponseLoginWeb | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const login = (userData: ResponseLoginWeb) => {
    console.log('AuthContext login called with:', userData);
    
    // Lưu vào localStorage trước
    AuthService.setUserData(userData);
    
    // Cập nhật state ngay lập tức
    setUser(userData);
    setIsAuthenticated(true);
    
    console.log('AuthContext state updated immediately:', { 
      isAuthenticated: true, 
      user: userData,
      fullName: userData.fullName || userData.tblDmEmployee?.fullname
    });
  };

  const logout = () => {
    console.log('AuthContext logout called'); // Debug log
    setUser(null);
    setIsAuthenticated(false);
    AuthService.removeToken();
  };

  const checkAuthStatus = useCallback(() => {
    console.log('Checking auth status...');
    
    const token = AuthService.getToken();
    const userData = AuthService.getUserData();
    
    console.log('Raw localStorage data:', {
      token: token,
      userData: userData,
      tokenLength: token?.length || 0
    });
    
    const isAuth = AuthService.isAuthenticated();
    
    console.log('Auth check result:', { isAuth, hasToken: !!token, hasUserData: !!userData });
    
    if (isAuth && userData && token && token.trim() !== '') {
      setUser(userData);
      setIsAuthenticated(true);
      
      if (AuthService.shouldRefreshToken()) {
        console.warn('Token sắp hết hạn, cần refresh token');
      }
    } else {
      console.log('Authentication failed, clearing state');
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    // Chỉ check auth khi mount, không check liên tục
    checkAuthStatus();
  }, [checkAuthStatus]);

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, checkAuthStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
